import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

/**
 * Utilities copied from previous versions of the application code
 * for compatibility with Studio Documents on outdated schemas.
 */

interface Formula {
  readonly type: "FORMULA";
  readonly formula: string;
}

function formulaNew(formula: string): Formula {
  return { type: "FORMULA", formula };
}

function isFormula(v: any): v is Formula {
  return !!v && typeof v === "object" && v.hasOwnProperty("type") && v.type === "FORMULA";
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_39: DocMigration = {
  description: "Remove BlockLayouts from the file, replacing with param disabled blocks",
  maxSchemaVersion: "0.0.39",
  apply: migrateDoc0039,
};

function migrateDoc0039GetBlockLayoutParamId(blockId: string) {
  return `${blockId.replace("-", "")}BlockLayout`;
}

function migrateDoc0039Design(json: any, oldRoomIdToNewRoomId: { [roomId: string]: string }) {
  const defaultDesign = Object.freeze({});

  if (json.design && json.design !== defaultDesign) {
    json.design.roomParamValues = json.design.roomLayoutParamValues;
    delete json.design.roomLayoutParamValues;

    if (json.design.roomParamValues) {
      for (const roomId of Object.keys(json.design.roomParamValues)) {
        if (typeof oldRoomIdToNewRoomId[roomId]) {
          json.design.roomParamValues[oldRoomIdToNewRoomId[roomId]] = json.design.roomParamValues[roomId];
        }
        delete json.design.roomParamValues[roomId];
      }
    }

    if (json.design.blockParamValues) {
      for (const blockId in json.design.blockParamValues) {
        const blockLayoutParamId = migrateDoc0039GetBlockLayoutParamId(blockId);
        json.design.partiParamValues[blockLayoutParamId] = json.design.blockParamValues[blockId].layout;
      }
    }

    delete json.design.blockParamValues;
  }
}

function migrateDoc0039(json: any) {
  const oldRoomIdToNewRoomId: { [roomId: string]: string } = {};
  const oldBlockToNewBlockIds: { [blockId: string]: { [blockLayoutId: string]: string } } = {};
  const oldBlocks = json.blocks ?? {};
  const newBlocks = {};

  for (const blockId of Object.keys(oldBlocks)) {
    const block = oldBlocks[blockId];
    if (!block) {
      continue;
    }

    if (!block.layouts) {
      continue;
    }

    const oldBlockLayoutIds = Object.keys(block.layouts);

    const blockLayoutParamId = migrateDoc0039GetBlockLayoutParamId(blockId);

    // if there are multiple layouts, we need to take the existing block
    // parameter and turn it into a parameter
    if (block.layouts && oldBlockLayoutIds.length > 1) {
      json.params[blockLayoutParamId] = block.params.layout;
    }

    const newBlockIds = {};

    // then take the existing block layouts and turn them into distinct blocks
    for (const blockLayoutId in block.layouts) {
      const newBlockId = uid();
      newBlockIds[blockLayoutId] = newBlockId;

      const newBlock = block.layouts[blockLayoutId];
      newBlocks[newBlockId] = newBlock;

      delete newBlock.price;
      delete newBlock.description;
      delete newBlock.builderOptionID;

      newBlock.name = [block.name, oldBlockLayoutIds.length === 1 ? undefined : newBlock.name]
        .filter((x) => x !== undefined)
        .join(" - ");
      newBlock.isHalfStory = block.isHalfStory;
      newBlock.levelId = block.levelId;

      for (const roomId in newBlock.rooms) {
        oldRoomIdToNewRoomId[blockId + "-" + roomId] = newBlockId + "-" + roomId;
      }

      // for each of those blocks, we need to add a disabled field that uses the parameter
      // to define whether the block is disabled or not

      if (oldBlockLayoutIds.length === 1) {
        // there is only one block layout, so we can safely copy the block's disabled field to the new block
        // as they map 1:1
        newBlock.disabled = block.disabled;
      } else {
        if (isFormula(block.disabled) && block.disabled.formula !== "false") {
          const formula = block.disabled.formula;
          newBlock.disabled = formulaNew(`${formula} or ${blockLayoutParamId} != "${blockLayoutId}"`);
        } else {
          newBlock.disabled = formulaNew(`${blockLayoutParamId} != "${blockLayoutId}"`);
        }
      }

      newBlock.type = "STATIC";
    }

    oldBlockToNewBlockIds[blockId] = newBlockIds;
  }

  json.blocks = newBlocks;

  // for each of the roof systems referencing the old block, update the references to reference the new one

  function repairBlockList(blocks: string[]) {
    return blocks.reduce(
      (a: string[], b: string) => a.concat(oldBlockToNewBlockIds[b] ? Object.values(oldBlockToNewBlockIds[b]) : []),
      [],
    );
  }

  for (const roofId in json.roofSystems) {
    const roofSystem = json.roofSystems[roofId];
    if (roofSystem.type === "MULTI_GABLE") {
      for (const roof of roofSystem.subRoofs) {
        if (Array.isArray(roof.blocks)) {
          roof.blocks = repairBlockList(roof.blocks);
        }
      }
    } else if (roofSystem.type === "SHED") {
      roofSystem.blocks = repairBlockList(roofSystem.blocks);
    } else if (roofSystem.type === "STRAIGHT_SKELETON") {
      roofSystem.blocks = repairBlockList(roofSystem.blocks);
    }
  }

  function convertRoomHandleHash(roomId: string) {
    if (!(roomId.startsWith("room."))) {
      console.assert(false, roomId);
    }
    const idSplit = roomId.split(".");
    if (!(idSplit.length === 4)) {
      console.assert(false);
    }
    const blockId = idSplit[1];
    const blockLayoutId = idSplit[2];
    const locationId = idSplit[3];
    if (!oldBlockToNewBlockIds[blockId] || !oldBlockToNewBlockIds[blockId][blockLayoutId]) {
      return null;
    }

    return `room.${oldBlockToNewBlockIds[blockId][blockLayoutId]}.${locationId}`;
  }

  function convertRoomHandleHashTable(elems: { [key: string]: any }) {
    if (!elems) {
      return;
    }

    for (const roomHash of Object.keys(elems)) {
      const val = elems[roomHash];
      delete elems[roomHash];
      const hash = convertRoomHandleHash(roomHash);
      if (!hash) {
        continue;
      }

      elems[hash] = val;
    }
  }

  const recursivelyConvertRoomIds = (v: any) => {
    if (typeof v === "object") {
      if (!v) {
        return;
      }

      if (v instanceof Array) {
        return v.forEach((x) => recursivelyConvertRoomIds(x));
      }

      for (const key in v) {
        if (!v.hasOwnProperty(key)) {
          continue;
        }

        if (key === "roomId" && typeof v[key] === "string" && typeof oldRoomIdToNewRoomId[v[key]] === "string") {
          v[key] = oldRoomIdToNewRoomId[v[key]];
        } else {
          recursivelyConvertRoomIds(v[key]);
        }
      }
    }
  };

  if (json.electricalSystem) {
    recursivelyConvertRoomIds(json.electricalSystem);

    convertRoomHandleHashTable(json.electricalSystem.roomClearRules);
    convertRoomHandleHashTable(json.electricalSystem.roomEmbeddedGroups);
    convertRoomHandleHashTable(json.electricalSystem.roomIndependentLoadArrays);
  }

  // hidden or locked may reference a block layout that is gone from the document

  const newHidden = {};

  for (const hash in json.hidden) {
    const handle = json.hidden[hash];
    if (handle.type === "BLOCK_LAYOUT") {
      continue;
    }
    newHidden[hash] = handle;
  }

  json.hidden = newHidden;

  const newLocked = {};

  for (const hash in json.locked) {
    const handle = json.locked[hash];
    if (handle.type === "BLOCK_LAYOUT") {
      continue;
    }
    newHidden[hash] = handle;
  }

  json.newLocked = newLocked;

  // the design may reference an old state of block activation

  migrateDoc0039Design(json, oldRoomIdToNewRoomId);

  return json;
}

export default migration_0_0_39;
